import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';

import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import { CONDITIONS, KEYS } from '../utils/constants';
import { usePaywallSkip } from '../utils/usePaywallExit';

import './assistant-section.scss';

const INTERSECTION_THRESHOLD = 0.35;
const KNOWN_PATHS = Object.values(CONDITIONS);

const AssistantSection = ({ path }) => {
  const { t } = useTranslation();
  const [userName] = useLocalStorage(KEYS.USER_NAME);
  const [storedPath] = useLocalStorage(KEYS.PATH);
  const handleSkip = usePaywallSkip('assistant_section');
  const sectionRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isStatic, setIsStatic] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (typeof IntersectionObserver === 'undefined' || !node) {
      setIsStatic(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsPlaying(true);
          observer.disconnect();
        }
      },
      { threshold: INTERSECTION_THRESHOLD },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  let conversationPath = CONDITIONS.INCONTINENCE;
  if (KNOWN_PATHS.includes(path)) conversationPath = path;
  else if (KNOWN_PATHS.includes(storedPath)) conversationPath = storedPath;

  const conversationKey = (key) => `evieOnboarding.assistantSection.${conversationPath}.${key}`;
  const greeting = userName
    ? t(conversationKey('greeting'), { name: userName })
    : t(conversationKey('greetingNoName'));

  const sectionClassName = ['assistant-section', isPlaying && 'is-playing', isStatic && 'is-static']
    .filter(Boolean)
    .join(' ');

  return (
    <section className={sectionClassName} ref={sectionRef}>
      <div className="assistant-section__content">
        <div className="assistant-section__chip-wrap">
          <span className="assistant-section__chip">{t('evieOnboarding.assistantSection.chip')}</span>
        </div>
        <h2 className="assistant-section__heading">
          <Trans components={{ br: <br /> }}>evieOnboarding.assistantSection.heading</Trans>
        </h2>
        <div className="assistant-section__card">
          <div className="assistant-section__header">
            <div className="assistant-section__avatar">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 20s-7-4.35-7-9.5C5 7.5 7 5.5 9.5 5.5c1.4 0 2.5.9 2.5 .9s1.1-.9 2.5-.9C17 5.5 19 7.5 19 10.5 19 15.65 12 20 12 20Z"
                  fill="#9b2c51"
                />
              </svg>
            </div>
            <div className="assistant-section__header-info">
              <p className="assistant-section__name">{t('evieOnboarding.assistantSection.assistantName')}</p>
              <p className="assistant-section__status">
                <span className="assistant-section__status-dot" />
                {t('evieOnboarding.assistantSection.status')}
              </p>
            </div>
          </div>
          <div className="assistant-section__body">
            <div className="assistant-section__row assistant-section__row--start assistant-section__beat assistant-section__beat--1">
              <div className="assistant-section__bubble-wrap">
                <div className="assistant-section__bubble assistant-section__bubble--bot">{greeting}</div>
                <p className="assistant-section__timestamp">{t('evieOnboarding.assistantSection.timestampGreeting')}</p>
              </div>
            </div>
            <div className="assistant-section__chips assistant-section__beat assistant-section__beat--2">
              <span className="assistant-section__chip-option">{t(conversationKey('chipOther'))}</span>
              <span className="assistant-section__chip-option assistant-section__chip-option--selected">
                {t(conversationKey('chipSelected'))}
              </span>
            </div>
            <div className="assistant-section__row assistant-section__row--end assistant-section__beat assistant-section__beat--3">
              <div className="assistant-section__bubble-wrap">
                <div className="assistant-section__bubble assistant-section__bubble--user">
                  {t(conversationKey('userMessage'))}
                </div>
                <p className="assistant-section__timestamp assistant-section__timestamp--right">
                  {t('evieOnboarding.assistantSection.timestampReply')}
                </p>
              </div>
            </div>
            <div className="assistant-section__row assistant-section__row--start assistant-section__typing">
              <div className="assistant-section__typing-bubble">•••</div>
            </div>
            <div className="assistant-section__row assistant-section__row--start assistant-section__beat assistant-section__beat--4">
              <div className="assistant-section__bubble-wrap">
                <div className="assistant-section__bubble assistant-section__bubble--bot">
                  {t(conversationKey('answer'))}
                </div>
              </div>
            </div>
            <div className="assistant-section__row assistant-section__row--start assistant-section__beat assistant-section__beat--5">
              <div className="assistant-section__bubble-wrap">
                <div className="assistant-section__exercise">
                  <div className="assistant-section__exercise-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="6" r="2.4" stroke="#9b2c51" strokeWidth="1.8" />
                      <path
                        d="M12 9v5l-3.5 5M12 14l3.5 5M6 11.5l6-1.8 6 1.8"
                        stroke="#9b2c51"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="assistant-section__exercise-title">{t(conversationKey('exerciseTitle'))}</p>
                    <p className="assistant-section__exercise-subtitle">{t(conversationKey('exerciseSubtitle'))}</p>
                  </div>
                </div>
                <p className="assistant-section__timestamp">{t('evieOnboarding.assistantSection.timestampReply')}</p>
              </div>
            </div>
          </div>
          <div className="assistant-section__input">
            <div className="assistant-section__input-field">{t('evieOnboarding.assistantSection.inputPlaceholder')}</div>
            <div className="assistant-section__input-send">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 12h14M13 6l6 6-6 6"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
        <div className="assistant-section__trust">
          <div className="assistant-section__trust-card">
            <p className="assistant-section__trust-title">{t('evieOnboarding.assistantSection.trust.availabilityTitle')}</p>
            <p className="assistant-section__trust-subtitle">
              {t('evieOnboarding.assistantSection.trust.availabilitySubtitle')}
            </p>
          </div>
          <div className="assistant-section__trust-card">
            <p className="assistant-section__trust-title">{t('evieOnboarding.assistantSection.trust.expertsTitle')}</p>
            <p className="assistant-section__trust-subtitle">
              {t('evieOnboarding.assistantSection.trust.expertsSubtitle')}
            </p>
          </div>
        </div>
        <div className="assistant-section__cta-wrap">
          <button type="button" className="assistant-section__cta" onClick={handleSkip}>
            {t('evieOnboarding.assistantSection.cta')}
          </button>
        </div>
        <p className="assistant-section__disclosure">{t('evieOnboarding.assistantSection.disclosure')}</p>
      </div>
    </section>
  );
};

AssistantSection.defaultProps = {
  path: null,
};

AssistantSection.propTypes = {
  path: PropTypes.oneOf(Object.values(CONDITIONS)),
};

export default AssistantSection;
